import { InvoiceApi } from 'axiosApi/apis/invoice-api';
import { setApiKeyToObject } from 'axiosApi/common';
import { AxiosResponse } from 'axios';
import axios from 'axios';

export interface CreateSigningDocumentRequest {
  title: string;
  description?: string;
}

export interface SignerInput {
  email: string;
  name?: string;
  signerOrder: number;
}

export interface AddSignersRequest {
  signers: SignerInput[];
  signingOrder: boolean;
}

export interface FieldInput {
  idSigningDocumentSigner: number;
  fieldType: 'signature' | 'initials' | 'text' | 'date' | 'signerName' | 'signerEmail' | 'current_date' | 'current_datetime';
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
}

export interface FieldValueInput {
  fieldId: number;
  value: string;
}

export interface SubmitSignatureRequest {
  fields: FieldValueInput[];
}

export interface RejectSigningRequest {
  reason?: string;
}

export class DocumentSigningApiWrapper {
  private invoiceApi: InvoiceApi;

  constructor(invoiceApi: InvoiceApi) {
    this.invoiceApi = invoiceApi;
  }

  private get axiosInstance() {
    return (this.invoiceApi as any).axios;
  }

  private get basePath(): string {
    return (this.invoiceApi as any).basePath;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const configuration = (this.invoiceApi as any).configuration;
    const headers: Record<string, string> = {};
    await setApiKeyToObject(headers, 'Userentity', configuration);
    await setApiKeyToObject(headers, 'Referer', configuration);
    await setApiKeyToObject(headers, 'Authorization', configuration);
    return headers;
  }

  async searchMyDocuments(version: string, term?: string, pageNum = 1, pageSize = 25): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.get(`${this.basePath}/api/v${version}/SigningDocument/my-documents`, {
      params: { term, pageNum, pageSize },
      headers,
    });
  }

  async searchDocuments(version: string, term?: string, pageNum = 1, pageSize = 25): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.get(`${this.basePath}/api/v${version}/SigningDocument`, {
      params: { term, pageNum, pageSize },
      headers,
    });
  }

  async getDocument(version: string, id: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.get(`${this.basePath}/api/v${version}/SigningDocument/${id}`, { headers });
  }

  async uploadDocument(version: string, id: number, file: File): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    const formData = new FormData();
    formData.append('file', file);
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningDocument/upload?documentId=${id}`, formData, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });
  }

  async createDocument(version: string, body: CreateSigningDocumentRequest): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningDocument`, body, { headers });
  }

  async addSigners(version: string, id: number, body: AddSignersRequest): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningDocument/${id}/signers`, body, { headers });
  }

  async saveFields(version: string, id: number, fields: FieldInput[]): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningField/${id}/fields`, { fields }, { headers });
  }

  async sendInvitations(version: string, id: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningDocument/${id}/send`, {}, { headers });
  }

  async resendInvitation(version: string, id: number, signerId: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.post(`${this.basePath}/api/v${version}/SigningDocument/${id}/resend/${signerId}`, {}, { headers });
  }

  async cancelDocument(version: string, id: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.delete(`${this.basePath}/api/v${version}/SigningDocument/${id}`, { headers });
  }

  async getAuditLog(version: string, id: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.get(`${this.basePath}/api/v${version}/SigningDocument/${id}/audit`, { headers });
  }

  async getFileBlob(version: string, id: number): Promise<string> {
    const headers = await this.buildHeaders();
    const response = await this.axiosInstance.get(
      `${this.basePath}/api/v${version}/SigningDocument/${id}/file`,
      { headers, responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
  }

  async getFields(version: string, id: number): Promise<AxiosResponse<any>> {
    const headers = await this.buildHeaders();
    return this.axiosInstance.get(`${this.basePath}/api/v${version}/SigningField/${id}/fields`, { headers });
  }

  // ── Public endpoints (no auth headers) ───────────────────────────────────

  getFileUrlByToken(version: string, token: string): string {
    return `${this.basePath}/api/v${version}/PublicSign/${token}/file`;
  }

  async getDocumentByToken(version: string, token: string): Promise<AxiosResponse<any>> {
    return axios.get(`${this.basePath}/api/v${version}/PublicSign/${token}`);
  }

  async getSignerProfile(version: string, token: string): Promise<AxiosResponse<any>> {
    return axios.get(`${this.basePath}/api/v${version}/PublicSign/${token}/signer-profile`);
  }

  async submitSignature(version: string, token: string, body: SubmitSignatureRequest): Promise<AxiosResponse<any>> {
    return axios.post(`${this.basePath}/api/v${version}/PublicSign/${token}/sign`, body);
  }

  async rejectSigning(version: string, token: string, body: RejectSigningRequest): Promise<AxiosResponse<any>> {
    return axios.post(`${this.basePath}/api/v${version}/PublicSign/${token}/reject`, body);
  }
}
